import { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApplicationStore } from '../store/application.store';
import { useNavigationStore } from '../store/navigation.store';
import { useApplicationFormSetup } from '../hooks/application-form-setup.hook';
import { useTenantContext } from '@/features/domains/hooks/use-tenant-context.hook';
import { setTenantFavicon } from '../utils/favicon.utils';
import { FormContent } from './FormContent';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { logger } from '@/features/help/utils/logger';
import { NotFound } from '@/features/help/components/NotFound';
import { TrialEnded } from './misc/TrialEnded';
import { FieldValue, FieldType } from '../types/forms';
import { useValidationStore } from '../store/validation.store';
import { resolveAutoNavigationTargetPageIndex } from '../utils/auto-navigation.utils';
import { getApplicationBrandTheme } from '../utils/brand-theme.utils';

/**
 * Checks if a field value is considered "empty" based on its type.
 * - String-based fields: empty if null, undefined, or ''
 * - File fields: empty if null, undefined, or empty array
 * - Boolean fields (checkbox, terms): considered "filled" if value is a boolean (user made a choice)
 * - Signature fields: empty if null, undefined, or ''
 */
function isFieldEmpty(value: FieldValue, fieldType: FieldType): boolean {
  // Boolean fields (checkbox, terms) are considered "filled" if user made a choice (true or false)
  if (fieldType === 'checkbox' || fieldType === 'terms') {
    return typeof value !== 'boolean';
  }

  // File fields: empty if null, undefined, or empty array
  if (fieldType === 'file') {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    return false; // Has a File object
  }

  // String-based fields (text, email, phone, date, signature, etc.): empty if null, undefined, or ''
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';

  return false;
}

// Purpose: Determine whether a required field is missing/invalid enough to block progress
function isRequiredFieldIncomplete(field: { type: FieldType; required?: boolean; value: FieldValue; crmPicklistValues?: any[]; currentPicklistOptions?: any[] }) {
  if (!field.required) return false;

  const value = field.value;

  if (field.type === 'terms') return value !== true;
  if (field.type === 'checkbox') return typeof value !== 'boolean';

  if (field.type === 'file') {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  }

  if (field.type === 'signature') {
    return !value || typeof value !== 'string' || !value.startsWith('data:image/');
  }

  if (field.type === 'picklist') {
    if (value === null || value === undefined) return true;
    if (typeof value !== 'string') return true;
    const trimmed = value.trim();
    if (!trimmed) return true;

    const allowedValues =
      Array.isArray(field.crmPicklistValues) && field.crmPicklistValues.length > 0
        ? field.crmPicklistValues.map((o: any) => String(o.value))
        : Array.isArray(field.currentPicklistOptions)
          ? field.currentPicklistOptions.map((o: any) => String(o.label))
          : [];

    return allowedValues.length > 0 ? !allowedValues.includes(trimmed) : false;
  }

  // String-ish types
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';

  return false;
}

// Purpose: Main component that handles the application form display and initialization
export function ApplicationForm() {
  const navigate = useNavigate();
  const { userShortId, mappingShortId } = useParams<{
    userShortId: string;
    mappingShortId: string;
  }>();
  const [searchParams] = useSearchParams();
  const crmId = searchParams.get('crmId') || undefined;
  const signerIndexParam = searchParams.get('signerIndex');
  const signerIndex = signerIndexParam != null ? Number(signerIndexParam) : undefined;
  const recordOwnerId = searchParams.get('recordOwnerId') || undefined;
  const recordId = crmId; // Maintain local naming for downstream logs and hook
  const signerShortId = undefined; // signerShortId is deprecated in favor of signerIndex

  const {
    error,
    structure,
    isBrokerTrialEnded,
    pdfOnly,
    setRecordOwnerId,
    editableFields,
    currentSignerCompleted,
  } = useApplicationStore();
  const { getVisiblePages, setActivePage } = useNavigationStore();
  const { clearInvalidFields } = useValidationStore();
  const hasCrmId = typeof crmId === 'string' && crmId.length > 0;
  const hasSignerIndex = typeof signerIndex === 'number' && !Number.isNaN(signerIndex);
  const hasHydratedSignerContext = hasCrmId && hasSignerIndex;
  
  // Store recordOwnerId in application store when provided via URL
  useEffect(() => {
    if (recordOwnerId) {
      setRecordOwnerId(recordOwnerId);
    }
  }, [recordOwnerId, setRecordOwnerId]);
  
  // Custom domain validation - check if domain owner matches URL userShortId
  const { isCustomDomain, userShortId: tenantUserShortId, isLoading: isTenantLoading, error: tenantError } = useTenantContext();

  // Purpose: Update favicon based on tenant logo (custom domain or shortId).
  useEffect(() => {
    if (!userShortId) {
      return;
    }

    void setTenantFavicon({
      domain: window.location.hostname,
      userShortId,
    });
  }, [userShortId]);

  // Use the custom hook to handle all setup logic
  const { isObjectTypeCheckLoading, isObjectTypeInvalid, imageLoading, loadingState, isReady } =
    useApplicationFormSetup({
      userShortId,
      mappingShortId,
      signerShortId,
      recordId,
      // Only pass signerIndex when crmId is present; signerIndex is not meaningful for "blank application" links.
      signerIndex:
        typeof recordId === 'string' && recordId.length > 0 && typeof signerIndex === 'number'
          ? signerIndex
          : undefined,
    });
  const loaderBrandTheme = getApplicationBrandTheme(undefined, userShortId);
  const loaderBrandProps = loaderBrandTheme.logoOnDarkUrl
    ? {
        backgroundColor: loaderBrandTheme.surfaceBackground,
        foregroundColor: loaderBrandTheme.surfaceForeground,
        mutedForegroundColor: loaderBrandTheme.surfaceMutedForeground,
        accentColor: loaderBrandTheme.actionBackground,
        logoUrl: loaderBrandTheme.logoOnDarkUrl,
        logoAlt: 'LendNav',
      }
    : {};
  const renderApplicationLoader = (message: string) => (
    <FullScreenLoader message={message} {...loaderBrandProps} />
  );

  // Auto-jump to first page with empty editable fields (one-time), must be declared before any early returns
  const hasAutoNavigatedRef = useRef(false);
  useEffect(() => {
    if (!isReady || pdfOnly || hasAutoNavigatedRef.current || !structure?.pages?.length) return;
    try {
      const visiblePages = getVisiblePages();
      if (!visiblePages.length) return;

      // Create a Set of editable field IDs for fast lookup
      const editableFieldSet = new Set(editableFields);
      const documentUploadPageIndex = visiblePages.findIndex((page) => page.id === 'document-upload');
      const hasDocumentUploadPage = documentUploadPageIndex >= 0;
      const shouldSkipDocumentUploadForIncompleteSigner =
        hasDocumentUploadPage && hasHydratedSignerContext && !currentSignerCompleted;
      const pageSearchLimit = shouldSkipDocumentUploadForIncompleteSigner
        ? documentUploadPageIndex
        : visiblePages.length;

      // Find the first page that has at least one incomplete REQUIRED editable field
      // and the first page with any empty editable field in a single pass.
      let firstRequiredIncompletePageIndex = -1;
      let firstEmptyEditablePageIndex = -1;

      for (let pageIndex = 0; pageIndex < pageSearchLimit; pageIndex++) {
        const page = visiblePages[pageIndex];

        for (const section of page.sections || []) {
          for (const field of section.fields || []) {
            if (
              firstRequiredIncompletePageIndex < 0 &&
              editableFieldSet.has(field.id) &&
              isRequiredFieldIncomplete(field)
            ) {
              firstRequiredIncompletePageIndex = pageIndex;
            }

            if (
              firstEmptyEditablePageIndex < 0 &&
              editableFieldSet.has(field.id) &&
              isFieldEmpty(field.value, field.type)
            ) {
              firstEmptyEditablePageIndex = pageIndex;
            }

            if (firstRequiredIncompletePageIndex >= 0 && firstEmptyEditablePageIndex >= 0) {
              break;
            }
          }

          if (firstRequiredIncompletePageIndex >= 0 && firstEmptyEditablePageIndex >= 0) {
            break;
          }
        }

        if (firstRequiredIncompletePageIndex >= 0 && firstEmptyEditablePageIndex >= 0) {
          break;
        }
      }

      // If a hydrated signer has already completed, prefer document-upload when it is visible.
      if (hasHydratedSignerContext && currentSignerCompleted && hasDocumentUploadPage) {
        setActivePage(documentUploadPageIndex);
        hasAutoNavigatedRef.current = true;
        clearInvalidFields();
        logger.info('Application Form', 'Auto-navigated completed signer to document-upload page', {
          targetPageId: 'document-upload',
          targetPageIndex: documentUploadPageIndex,
          totalVisiblePages: visiblePages.length,
        });
        return;
      }

      // If completed signer has no upload page, always show submission-complete screen on reload.
      if (
        hasHydratedSignerContext &&
        currentSignerCompleted &&
        !hasDocumentUploadPage &&
        userShortId &&
        mappingShortId
      ) {
        hasAutoNavigatedRef.current = true;
        const completionPath = `/application/${userShortId}/${mappingShortId}/submission-complete${
          window.location.search || ''
        }`;
        logger.info(
          'Application Form',
          'Redirecting completed signer to submission-complete page (no visible document-upload page)',
          {
            completionPath,
            totalVisiblePages: visiblePages.length,
          }
        );
        navigate(completionPath, { replace: true });
        return;
      }

      const targetPageIndex = resolveAutoNavigationTargetPageIndex({
        visiblePages,
        firstRequiredIncompletePageIndex,
        firstEmptyEditablePageIndex,
        hasHydratedSignerContext,
        currentSignerCompleted,
      });

      const targetPageId = visiblePages[targetPageIndex]?.id;
      let emptyFieldId: string | undefined;
      if (firstRequiredIncompletePageIndex >= 0) {
        const targetPage = visiblePages[firstRequiredIncompletePageIndex];
        for (const section of targetPage?.sections || []) {
          for (const field of section.fields || []) {
            if (editableFieldSet.has(field.id) && isRequiredFieldIncomplete(field)) {
              emptyFieldId = field.id;
              break;
            }
          }
          if (emptyFieldId) break;
        }
      } else if (firstEmptyEditablePageIndex >= 0) {
        const targetPage = visiblePages[firstEmptyEditablePageIndex];
        for (const section of targetPage?.sections || []) {
          for (const field of section.fields || []) {
            if (editableFieldSet.has(field.id) && isFieldEmpty(field.value, field.type)) {
              emptyFieldId = field.id;
              break;
            }
          }
          if (emptyFieldId) break;
        }
      }

      setActivePage(targetPageIndex);
      hasAutoNavigatedRef.current = true;

      // Ensure we don't show validation errors just from initial auto-navigation
      clearInvalidFields();

      logger.info('Application Form', 'Auto-navigated to first page with empty editable field', {
        targetPageId,
        targetPageIndex,
        emptyFieldId: emptyFieldId || 'none (all fields filled)',
        totalVisiblePages: visiblePages.length,
      });
    } catch (e) {
      logger.warn(
        'Application Form',
        'Failed to auto-navigate based on empty fields (non-blocking)',
        { e }
      );
    }
  }, [
    isReady,
    pdfOnly,
    structure,
    editableFields,
    clearInvalidFields,
    getVisiblePages,
    setActivePage,
    hasHydratedSignerContext,
    currentSignerCompleted,
    navigate,
    userShortId,
    mappingShortId,
  ]);

  // If signerIndex is present without crmId, ignore it (robustness for mistakenly generated/shared URLs)
  if (!hasCrmId && hasSignerIndex) {
    logger.warn('Application Form', 'Ignoring signerIndex without crmId (blank application link)', {
      userShortId,
      mappingShortId,
      signerIndex,
      path: window.location.pathname,
      search: window.location.search,
    });
  }

  // Custom domain validation: if on a custom domain, the URL userShortId must match the domain owner
  if (isTenantLoading) {
    return renderApplicationLoader('Verifying domain...');
  }
  
  if (isCustomDomain) {
    // If there's an error during lookup or no tenant found for the custom domain
    if (tenantError || !tenantUserShortId) {
      logger.warn('Application Form', 'Domain lookup failed or no tenant found for custom domain', {
        userShortId,
        tenantUserShortId,
        tenantError,
      });
      return <NotFound />;
    }
    
    // If the userShortId in the URL does not match the tenant owner of the custom domain
    if (userShortId !== tenantUserShortId) {
      logger.warn('Application Form', 'URL userShortId does not match custom domain owner', {
        urlUserShortId: userShortId,
        tenantUserShortId,
      });
      return <NotFound />;
    }
    
    logger.info('Application Form', 'Custom domain validated', { userShortId, tenantUserShortId });
  }

  // Show 404 if mapping is not for Lead object
  if (isObjectTypeInvalid) {
    logger.info('Application Form', 'Showing 404 due to invalid object type', {
      mappingShortId,
      hasStructure: !!structure,
      hasPages: !!structure?.pages?.length,
    });
    return <NotFound />;
  }

  // Show error state with support information (check error first before loading)
  if (error && !structure) {
    logger.error('Application Form', 'Showing error state due to explicit error', {
      error,
      hasStructure: !!structure,
    });
    return <NotFound />;
  }

  // Show trial ended message if broker's trial has ended (check before loading to avoid infinite spinner)
  if (isBrokerTrialEnded) {
    logger.warn('Application Form', 'Broker trial has ended', {
      userShortId,
      mappingShortId,
      recordId,
    });
    return <TrialEnded />;
  }

  // Show loading state while fetching data or loading images or checking object type
  if (loadingState || !structure || imageLoading || isObjectTypeCheckLoading) {
    return renderApplicationLoader('Loading application...');
  }

  // Show error if structure is invalid
  const visiblePages = getVisiblePages();
  if (visiblePages.length === 0) {
    logger.error('Application Form', 'Showing error state due to invalid structure', {
      hasStructure: !!structure,
      pagesLength: visiblePages.length,
      structure: structure || null,
    });
    return <NotFound />;
  }


  // The form is ready to be displayed
  if (isReady) {
    return (
      <div className="min-h-screen bg-gray-50 font-inter">
        <FormContent />
      </div>
    );
  }

  // Fallback loading state
  return renderApplicationLoader('Loading application...');
}

export default ApplicationForm;
